import { useEffect } from "react";
import { PencilIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { JobSignupPreview } from "@/components/tools/JobSignupPreview";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { shiftTypeToLabel } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS, MIXPANEL_SUBSCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { CompensationType } from "@/types";
import type { ShiftType, SignupVariantBadge } from "@/types";

export const ToolsSignupVariantsShowPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("No ID found in URL for signup variant show page!");
  }

  const { data: signupVariant, status } =
    novaClient.internal.admin.dynamicPageVariants.findOne.useQuery({
      id: parseInt(id),
    });

  // Get the job title for the selected job.
  const companies = useGetCachedCompaniesSummary();
  const payload = signupVariant?.payload;
  const jobId = payload?.type === "jobSpecific" ? payload.jobId : null;
  let jobTitle: string | null = null;
  if (jobId) {
    for (const company of companies) {
      for (const job of company.jobs) {
        if (job.id === jobId) {
          jobTitle = job.title;
          break;
        }
      }
      if (jobTitle) break;
    }
  }

  useSetTitle({
    title: "Tools | Signup Variants | Show",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.SIGNUP_VARIANTS, {
      subScreenName: MIXPANEL_SUBSCREENS.SHOW,
    });
  }, []);

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle className="!text-slate-500">
          <Link className="hover:underline" to="/tools/signup-variants">
            Signup Variants
          </Link>{" "}
          / <span className="text-slate-700">{id}</span>
        </TopbarTitle>
        <Button
          className="rounded-full"
          onClick={() => {
            void navigate(`/tools/signup-variants/${id}/edit`);
          }}
        >
          <PencilIcon className="h-4 w-4" />
          Edit
        </Button>
      </TopbarContainer>
      <div className="flex-1 overflow-hidden">
        {status === "pending" && (
          <div className="p-6">
            <LoadingIndicator variant="medium" />
          </div>
        )}
        {status === "error" && (
          <div className="p-6">
            <ErrorText errorMessage="Error loading signup variant." />
          </div>
        )}
        {status === "success" && (
          <Row className="h-full">
            <Column className="min-w-0 flex-1 gap-6 overflow-y-auto p-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-700">
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <div>
                    <Text variant="small">Name</Text>
                    <p className="text-slate-700">{signupVariant.name}</p>
                  </div>
                  <div>
                    <Text variant="small">Hash</Text>
                    <p className="text-sm text-slate-700">
                      {signupVariant.hash}
                    </p>
                  </div>
                  <div>
                    <Text variant="small">Type</Text>
                    <p className="text-slate-700">
                      {payload?.type === "jobSpecific"
                        ? "Job Specific"
                        : "Generic"}
                    </p>
                  </div>
                </CardContent>
              </Card>
              {payload?.type === "jobSpecific" && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-slate-700">
                      Job Association
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4">
                    <div>
                      <Text variant="small">Linked Job</Text>
                      <p className="text-slate-700">{jobTitle ?? "-"}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-700">
                    Variant Payload
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  {payload?.type === "jobSpecific" ? (
                    <>
                      <div>
                        <Text variant="small">Desired Position</Text>
                        <p className="text-slate-700">
                          {payload.desiredPosition}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">Hero Image</Text>
                        <img
                          alt="Hero preview"
                          className="h-24 rounded-md border object-cover"
                          src={payload.heroImageUrl}
                        />
                      </div>
                      <div>
                        <Text variant="small">Location</Text>
                        <p className="text-slate-700">{payload.location}</p>
                      </div>
                      <div>
                        <Text variant="small">Job Title</Text>
                        <p className="text-slate-700">{payload.jobTitle}</p>
                      </div>
                      <div>
                        <Text variant="small">Industry</Text>
                        <p className="text-slate-700">{payload.industry}</p>
                      </div>
                      <div>
                        <Text variant="small">Compensation</Text>
                        <p className="text-slate-700">
                          {`$${String(payload.compensationMin)} - $${String(payload.compensationMax)}${
                            payload.compensationType === CompensationType.YEARLY
                              ? "/yr"
                              : "/hr"
                          }`}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">Badges</Text>
                        {payload.badges.length > 0 ? (
                          <ul className="list-disc pl-4">
                            {payload.badges.map((badge: SignupVariantBadge) => (
                              <li className="text-slate-700" key={badge}>
                                {badge}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-slate-700">-</p>
                        )}
                      </div>
                      <div>
                        <Text variant="small">Shifts</Text>
                        {payload.shifts.length > 0 ? (
                          <ul className="list-disc pl-4">
                            {payload.shifts.map((s: string) => (
                              <li className="text-slate-700" key={s}>
                                {shiftTypeToLabel[s as ShiftType]}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-slate-700">-</p>
                        )}
                      </div>
                      <div>
                        <Text variant="small">The Work</Text>
                        <p className="text-slate-700">{payload.theWork}</p>
                      </div>
                      <div>
                        <Text variant="small">Relocation</Text>
                        <p className="text-slate-700">
                          {payload.relocationAvailable
                            ? `$${String(payload.relocationMin)} - $${String(payload.relocationMax)}`
                            : "Not available"}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">What We&apos;re Looking For</Text>
                        <p className="text-slate-700">
                          {payload.whatWereLookingFor}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">Why this Shop</Text>
                        <p className="text-slate-700">{payload.whyThisShop}</p>
                      </div>
                      <div>
                        <Text variant="small">CTA Label</Text>
                        <p className="text-slate-700">
                          {payload.ctaLabel ?? "-"}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">Button Text</Text>
                        <p className="text-slate-700">
                          {payload.buttonText ?? "-"}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">Screening Questions</Text>
                        {payload.screeningQuestions.length > 0 ? (
                          <ul className="list-disc pl-4">
                            {payload.screeningQuestions.map(
                              (
                                q: { question: string; required: boolean },
                                i: number,
                              ) => (
                                <li className="text-slate-700" key={i}>
                                  {q.question}
                                  {q.required && (
                                    <span className="text-xs text-red-500">
                                      *
                                    </span>
                                  )}
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <p className="text-slate-700">-</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <Text variant="small">Header</Text>
                        <p className="text-slate-700">
                          {payload?.header ?? "-"}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">Subheader</Text>
                        <p className="text-slate-700">
                          {payload?.subHeader ?? "-"}
                        </p>
                      </div>
                      <div>
                        <Text variant="small">Button Text</Text>
                        <p className="text-slate-700">
                          {payload?.buttonText ?? "-"}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </Column>
            {payload?.type === "jobSpecific" && (
              <Row className="items-center justify-center border-l border-slate-200 p-8">
                <div className="rounded-2xl bg-slate-100 p-8">
                  <JobSignupPreview
                    badges={payload.badges}
                    buttonText={payload.buttonText ?? ""}
                    compensationMax={payload.compensationMax.toString()}
                    compensationMin={payload.compensationMin.toString()}
                    compensationType={
                      payload.compensationType as CompensationType
                    }
                    ctaLabel={payload.ctaLabel ?? ""}
                    header={payload.jobTitle}
                    heroImageUrl={payload.heroImageUrl}
                    location={payload.location}
                    relocationAvailable={payload.relocationAvailable}
                    relocationMax={
                      payload.relocationAvailable
                        ? payload.relocationMax.toString()
                        : ""
                    }
                    relocationMin={
                      payload.relocationAvailable
                        ? payload.relocationMin.toString()
                        : ""
                    }
                    shifts={payload.shifts}
                    subHeader={payload.industry}
                    theWork={payload.theWork}
                    whatWereLookingFor={payload.whatWereLookingFor}
                    whyThisShop={payload.whyThisShop}
                  />
                </div>
              </Row>
            )}
          </Row>
        )}
      </div>
    </Column>
  );
};
